import React, { useRef, useState } from 'react'
import Modal from 'shared/ui/Modal/Modal'
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check-circle.svg'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import { ReactComponent as PlayIcon } from 'assets/icons/play.svg'
import { ReactComponent as TableIcon } from 'assets/icons/tableView.svg'
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg'
import {
	bulkImportParentContacts,
	downloadParentContactsImportTemplate,
} from 'api/parentCommunications'
import {
	InlineErrorBanner,
	USER_FRIENDLY_ERROR,
	toUserFriendlyError,
} from '../InlineErrorBanner/InlineErrorBanner'
import s from './ImportContactsModal.module.scss'

const TitleIcon = ({ complete }) => (
	<span className={s.titleIcon} aria-hidden="true">
		{complete ? <CheckCircleIcon /> : <PeopleIcon />}
	</span>
)

export const ImportContactsModal = ({ onClose, onImportComplete }) => {
	const inputRef = useRef(null)
	const [file, setFile] = useState(null)
	const [importStatus, setImportStatus] = useState('idle')
	const [result, setResult] = useState(null)
	const [error, setError] = useState(null)
	const [refreshError, setRefreshError] = useState(null)
	const [refreshing, setRefreshing] = useState(false)

	const refreshContacts = async () => {
		if (typeof onImportComplete !== 'function') return
		setRefreshing(true)
		setRefreshError(null)
		try {
			await onImportComplete()
		} catch (err) {
			setRefreshError(toUserFriendlyError(err, USER_FRIENDLY_ERROR))
		} finally {
			setRefreshing(false)
		}
	}

	const finishImportWithResult = async data => {
		setResult(data || {})
		setImportStatus('complete')
		setError(null)
		const importedCount = Number(data?.imported) || 0
		if (importedCount > 0) {
			await refreshContacts()
		}
	}

	const downloadTemplate = async () => {
		setError(null)
		try {
			const response = await downloadParentContactsImportTemplate()
			const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
			const link = document.createElement('a')
			link.href = URL.createObjectURL(blob)
			link.download = 'parent_contacts_template.csv'
			link.click()
			URL.revokeObjectURL(link.href)
		} catch (err) {
			setError(toUserFriendlyError(err))
		}
	}

	const selectFile = event => {
		const next = event.target.files?.[0] || null
		setFile(next)
		setImportStatus('idle')
		setResult(null)
		setError(null)
		setRefreshError(null)
	}

	const startImport = async () => {
		if (!file || importStatus === 'importing') return
		if (!file.name.toLowerCase().endsWith('.csv')) {
			setError('Only CSV files are supported')
			return
		}
		setImportStatus('importing')
		setError(null)
		setRefreshError(null)
		try {
			const response = await bulkImportParentContacts(file)
			await finishImportWithResult(response?.data || {})
		} catch (err) {
			const data = err?.response?.data
			if (data && (data.failed || data.message || data.imported != null)) {
				await finishImportWithResult(data)
			} else {
				setError(toUserFriendlyError(err))
				setImportStatus('idle')
			}
		}
	}

	const isComplete = importStatus === 'complete'
	const isImporting = importStatus === 'importing'

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={isComplete ? 'Import finished' : 'Import parent contacts'}
			iconTitle={() => <TitleIcon complete={isComplete} />}
			ariaLabel="Import parent contacts"
			width={540}
			maxHeight={420}
			compact
			closeButtonClassName={s.closeButton}
		>
			<div className={s.content}>
				{error ? <InlineErrorBanner message={error} /> : null}
				{isComplete ? (
					<section className={s.success} aria-live="polite">
						<span className={s.successIcon} aria-hidden="true">
							<CheckCircleIcon />
						</span>
						<h2 className={s.successTitle}>Import result</h2>
						<p className={s.successText}>{result?.message || 'Import completed.'}</p>
						{result?.imported != null ? (
							<p className={s.successText}>
								Imported: {result.imported}
								{Array.isArray(result.failed)
									? `, failed: ${result.failed.length}`
									: ''}
							</p>
						) : null}
						{Array.isArray(result?.failed) && result.failed.length > 0 ? (
							<ul className={s.failedList}>
								{result.failed.slice(0, 8).map(item => (
									<li key={`${item.row}-${item.email}`}>
										Row {item.row}: {item.email || '—'} — {item.reason}
									</li>
								))}
							</ul>
						) : null}
						{refreshError ? (
							<InlineErrorBanner
								message={refreshError}
								onRetry={refreshContacts}
								retrying={refreshing}
							/>
						) : null}
						<button type="button" className={s.closeResult} onClick={onClose}>
							Close
						</button>
					</section>
				) : (
					<>
						<section className={s.format} aria-labelledby="contact-file-format">
							<div className={s.formatHead}>
								<span className={s.formatIcon} aria-hidden="true">
									<TableIcon />
								</span>
								<div className={s.formatCopy}>
									<h3 id="contact-file-format" className={s.formatTitle}>
										File columns
									</h3>
									<p className={s.formatText}>
										Download the API template for the exact CSV format.
									</p>
								</div>
							</div>
							<div className={s.columns}>
								<code className={s.column}>student_id</code>
								<code className={s.column}>contact_name</code>
								<code className={s.column}>email</code>
								<code className={s.column}>contact_type</code>
								<code className={s.column}>is_primary</code>
							</div>
							<button type="button" className={s.download} onClick={downloadTemplate}>
								<span className={`${s.icon} ${s.iconDownload}`} aria-hidden="true">
									<UploadIcon />
								</span>
								Download CSV template
							</button>
						</section>
						<input
							ref={inputRef}
							className={s.fileInput}
							type="file"
							accept=".csv"
							onChange={selectFile}
						/>
						<button
							type="button"
							className={`${s.upload} ${file ? s.uploadSelected : ''}`.trim()}
							onClick={() => inputRef.current?.click()}
						>
							<span className={s.uploadIcon} aria-hidden="true">
								{file ? <CheckCircleIcon /> : <UploadIcon />}
							</span>
							<span className={s.uploadLabel}>{file?.name || 'Choose contact file'}</span>
							<span className={s.uploadHint}>
								{file ? 'Ready to import' : 'CSV only'}
							</span>
						</button>
						{file ? (
							<div className={s.importArea}>
								<button
									type="button"
									className={s.startImport}
									onClick={startImport}
									disabled={isImporting}
								>
									<span className={s.icon} aria-hidden="true">
										<PlayIcon />
									</span>
									{isImporting ? 'Importing…' : 'Start import'}
								</button>
							</div>
						) : null}
					</>
				)}
			</div>
		</Modal>
	)
}

export default ImportContactsModal
